// =============================================================================
// app/settings/data-privacy.tsx — data export + account deletion (bare-RN port).
//
// MIGRATION (P3 settings):
//   - @expo/vector-icons -> components/ui/Icon.
//   - expo-router (Stack/useRouter/router.replace) -> react-navigation: the inline
//     <Stack.Screen> header is dropped (SettingsStack owns it); after the account
//     is deleted, logout() flips the auth state and the root useAuth() gate swaps
//     to the AuthStack — we also defensively reset to Auth.
//
// APPLE-ALIGNED REDESIGN (matches Home/Search/Security shipped standard):
//   - Full-bleed <ParticleBackground/> aurora as the absoluteFill first child; the
//     screen surface is transparent so the aurora shows under the (SettingsStack)
//     translucent header and behind the scrolling content.
//   - Uniform glass surfaces (GlassCard) + design tokens; GlassButton for the
//     primary export CTA; typographyScale with tightened tracking on titles.
//   - Danger zone keeps its solid red intent (destructive affordances stay legible
//     and unmistakable per skill §16 — feedback: warning/error).
//
// ⚠️ SHIPPING GATE — DO NOT REGRESS: the Apple App-Store requirement is an
// in-app account-deletion path. The two-step confirm flow + the
// DELETE /users/me/account { confirmation: 'DELETE_MY_ACCOUNT' } call MUST stay
// intact exactly as below.
// =============================================================================
import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '../../components/ui/Icon';
import { api, logout } from '../../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard, GlassButton } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';

export default function DataPrivacyScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [exporting, setExporting] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  async function handleExportData() {
    Alert.alert(
      'Export Data',
      'We will prepare a download of all your CRYB data. You will receive an email when it is ready.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Export',
          onPress: async () => {
            setExporting(true);
            // Backend defines GET /users/me/export (no POST).
            const res = await api('/users/me/export', { method: 'GET' });
            setExporting(false);

            if (res.success) {
              Alert.alert(
                'Export Requested',
                'You will receive an email with a download link once your data is ready.'
              );
            } else {
              Alert.alert('Error', res.error || 'Failed to request data export.');
            }
          },
        },
      ]
    );
  }

  function handleStartDelete() {
    setDeleteStep(1);
  }

  function handleDeleteStep2() {
    Alert.alert(
      'Are you sure?',
      'This will permanently delete your account, posts, messages, and all associated data. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Continue',
          style: 'destructive',
          onPress: () => setDeleteStep(2),
        },
      ]
    );
  }

  async function handleFinalDelete() {
    if (deleteConfirmText !== 'DELETE') {
      Alert.alert('Error', 'Please type DELETE to confirm.');
      return;
    }

    setDeleting(true);
    // Backend requires the confirmation phrase; the bearer token authenticates
    // the user, so no password re-entry is needed.
    const res = await api('/users/me/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation: 'DELETE_MY_ACCOUNT' }),
    });
    setDeleting(false);

    if (res.success) {
      await logout();
      Alert.alert('Account Deleted', 'Your account has been permanently deleted.');
      // logout() flips the auth state -> the root useAuth() gate renders the
      // AuthStack. Defensively reset to the Login screen as well.
      navigation.reset({ index: 0, routes: [{ name: 'Auth', params: { screen: 'Login' } }] });
    } else {
      Alert.alert('Error', res.error || 'Failed to delete account.');
    }
  }

  function handleCancelDelete() {
    setDeleteStep(0);
    setDeleteConfirmText('');
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[styles.scrollContent, containerStyle]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Export Data */}
          <Text style={styles.sectionTitle}>Your Data</Text>
          <GlassCard contentStyle={styles.cardStack}>
            <View style={styles.infoRow}>
              <View style={styles.iconWrap}>
                <Ionicons name="download-outline" size={20} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Export Your Data</Text>
                <Text style={styles.desc}>
                  Download a copy of your posts, messages, profile info, and activity.
                </Text>
              </View>
            </View>
            <GlassButton
              title="Request Data Export"
              icon="cloud-download-outline"
              onPress={handleExportData}
              loading={exporting}
              disabled={exporting}
              variant="secondary"
              fullWidth
            />
          </GlassCard>

          {/* Delete Account */}
          <Text style={styles.sectionTitle}>Danger Zone</Text>
          <GlassCard contentStyle={styles.cardStack} style={styles.dangerCard}>
            {deleteStep === 0 && (
              <>
                <View style={styles.infoRow}>
                  <View style={styles.iconWrapDanger}>
                    <Ionicons name="warning-outline" size={20} color={colors.error} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.label}>Delete Account</Text>
                    <Text style={styles.desc}>
                      Permanently delete your account and all data. This action cannot be
                      undone.
                    </Text>
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.deleteBtn}
                  onPress={handleStartDelete}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel="Delete my account"
                >
                  <Ionicons name="trash-outline" size={18} color={colors.error} />
                  <Text style={styles.deleteBtnText}>Delete My Account</Text>
                </TouchableOpacity>
              </>
            )}

            {deleteStep === 1 && (
              <>
                <View style={styles.stepHeader}>
                  <Text style={styles.stepTitle}>Step 1 of 2</Text>
                  <Text style={styles.stepDesc}>
                    Please understand what will happen when you delete your account:
                  </Text>
                </View>
                <View style={styles.bulletList}>
                  {[
                    'All your posts and comments will be permanently removed',
                    'Your profile and username will become unavailable',
                    'All direct messages will be deleted',
                    'Your crypto watchlists and alerts will be erased',
                    'Community memberships will be revoked',
                  ].map((line) => (
                    <View key={line} style={styles.bulletRow}>
                      <Ionicons name="close-circle" size={16} color={colors.error} style={styles.bulletIcon} />
                      <Text style={styles.bullet}>{line}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.stepActions}>
                  <TouchableOpacity
                    style={styles.cancelBtn}
                    onPress={handleCancelDelete}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Cancel account deletion"
                  >
                    <Text style={styles.cancelBtnText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.deleteBtn, styles.stepActionBtn]}
                    onPress={handleDeleteStep2}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="I understand, continue to final confirmation"
                  >
                    <Text style={styles.deleteBtnText} numberOfLines={1}>I Understand, Continue</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {deleteStep === 2 && (
              <>
                <View style={styles.stepHeader}>
                  <Text style={styles.stepTitle}>Step 2 of 2 · Final Confirmation</Text>
                  <Text style={styles.stepDesc}>
                    Type DELETE below to permanently delete your account.
                  </Text>
                </View>
                <TextInput
                  style={styles.deleteInput}
                  value={deleteConfirmText}
                  onChangeText={setDeleteConfirmText}
                  placeholder="Type DELETE to confirm"
                  placeholderTextColor={colors.faint}
                  autoCapitalize="characters"
                  autoCorrect={false}
                />
                <View style={styles.stepActions}>
                  <TouchableOpacity
                    style={styles.cancelBtn}
                    onPress={handleCancelDelete}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Cancel account deletion"
                  >
                    <Text style={styles.cancelBtnText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.deleteBtnFinal,
                      deleteConfirmText !== 'DELETE' && styles.deleteBtnDisabled,
                    ]}
                    onPress={handleFinalDelete}
                    disabled={deleteConfirmText !== 'DELETE' || deleting}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Permanently delete account"
                    accessibilityState={{ disabled: deleteConfirmText !== 'DELETE' || deleting }}
                  >
                    {deleting ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Text style={styles.deleteBtnTextFinal}>
                        Permanently Delete
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
          </GlassCard>

          <View style={{ height: spacing.xxxl * 2 }} />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  scrollContent: {
    padding: spacing.lg,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    marginTop: spacing.lg,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  cardStack: {
    gap: spacing.md,
  },
  dangerCard: {
    borderColor: colors.error + '30',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconWrapDanger: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.error + '12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.ink,
    letterSpacing: -0.2,
    ...fonts.semibold,
  },
  desc: {
    ...typographyScale.caption,
    fontSize: 13,
    lineHeight: 18,
    color: colors.muted,
    marginTop: 4,
    ...fonts.regular,
  },
  deleteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.error + '40',
    backgroundColor: colors.error + '10',
  },
  // In the Step-1 action row this button sits beside the flex:1 Cancel button;
  // without its own flex the long "I Understand, Continue" label stays content-
  // sized and can overflow/crowd the row on narrow phones (Cancel already flexes,
  // and Step-2's final button flexes — this matches that balanced layout).
  stepActionBtn: {
    flex: 1,
  },
  deleteBtnDisabled: {
    opacity: 0.4,
  },
  deleteBtnText: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.error,
    ...fonts.semibold,
  },
  deleteBtnFinal: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.md,
    backgroundColor: colors.error,
  },
  deleteBtnTextFinal: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.white,
    ...fonts.bold,
  },
  stepHeader: {
    gap: spacing.xs,
  },
  stepTitle: {
    ...typographyScale.body,
    fontSize: 15,
    color: colors.error,
    letterSpacing: -0.2,
    ...fonts.bold,
  },
  stepDesc: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.ink2,
    lineHeight: 20,
    ...fonts.regular,
  },
  bulletList: {
    gap: spacing.sm,
    paddingLeft: spacing.xs,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  bulletIcon: {
    marginTop: 1,
  },
  bullet: {
    flex: 1,
    ...typographyScale.caption,
    fontSize: 13,
    lineHeight: 18,
    color: colors.ink2,
    ...fonts.regular,
  },
  stepActions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtnText: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.ink,
    ...fonts.semibold,
  },
  deleteInput: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.bg,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderWidth: 1,
    borderColor: colors.error + '40',
    textAlign: 'center',
    letterSpacing: 2,
    ...fonts.bold,
  },
});
